import React from 'react';
import {EntityList, EntityListColumn, EntityListProps} from './EntityList';
import {
	DefaultButton,
	DetailsRow,
	Dialog,
	DialogFooter,
	DialogType,
	IDetailsListProps,
	IDetailsRowStyles,
	IDragDropContext,
	IDragDropEvents,
	mergeStyles,
	PrimaryButton,
	ScrollablePane,
	SelectionMode,
	useTheme,
} from '@fluentui/react';
import {useCommand} from 'hooks';
import {useTranslation} from 'react-i18next';
import {EntityOfFormPanels} from 'components/EntityPage/EntityFormPanels.types';
import {useEntityPageTranslation} from 'components/EntityPage/hooksForEntityFormPanels';
import {useBoolean} from '@fluentui/react-hooks';
import {TFunction} from 'i18next';
import {alphabeticalSort} from 'helpers/sortHelpers';

interface EntityListDragDropProps<T extends EntityOfFormPanels> {
	items: any[];
	columns: EntityListColumn[];
	dragDropSortActivated: boolean;
	toggleDragDropSort: () => void;
	updateEntity: (item: T, oldItem?: T) => any;
	entityListProps?: Partial<EntityListProps>;
}

export function EntityListDragDrop<T extends EntityOfFormPanels>(
	props: EntityListDragDropProps<T>,
) {
	const {t} = useEntityPageTranslation();
	const {updateEntity, toggleDragDropSort} = props;

	const [items, setItems] = React.useState(props.items);
	const [draggedItem, setDraggedItem] = React.useState<any | undefined>(null);
	const [dialogVisible, {setTrue: showDialog}] = useBoolean(false);

	const theme = useTheme();
	const dragEnterClass = mergeStyles({
		backgroundColor: theme.palette.neutralLight,
	});

	const insertBeforeItem = React.useCallback(
		(item?: any) => {
			if (draggedItem && item) {
				const itemsWODraggedItem = items.filter(x => x.id !== draggedItem.id);
				const indexNew = itemsWODraggedItem.findIndex(x => x.id === item.id);
				setItems([
					...itemsWODraggedItem.slice(0, indexNew),
					draggedItem,
					...itemsWODraggedItem.slice(indexNew),
				]);
			}
		},
		[draggedItem, items],
	);

	const forceAlphabetic = React.useCallback(() => {
		setItems(alphabeticalSort(items, 'name'));
	}, [items]);

	const dragDropEvents: IDragDropEvents = React.useMemo(() => {
		return {
			canDrop(dropContext?: IDragDropContext, dragContext?: IDragDropContext) {
				return true;
			},
			canDrag(item?: any) {
				return true;
			},
			onDragEnter(item?: any, event?: DragEvent) {
				return dragEnterClass;
			},
			onDragLeave(item?: any, event?: DragEvent) {
				return null;
			},

			onDrop(item: any, event?: DragEvent) {
				insertBeforeItem(item);
			},
			onDragStart(
				item?: any,
				itemIndex?: number,
				selectedItems?: any[],
				event?: MouseEvent,
			) {
				setDraggedItem(item);
			},
			onDragEnd(item?: any, event?: DragEvent) {
				setDraggedItem(null);
			},
		};
	}, [insertBeforeItem]);

	const changedItems: boolean[] = React.useMemo(
		() =>
			items.map(
				(x, i) =>
					x.id !== props.items.at(i)!.id ||
					i + 1 !== props.items.at(i)!.sortPriority,
			),
		[items],
	);

	const propsFinal = React.useMemo(() => {
		return {
			...props,
			items: items.map((x, index) => {
				return {...x, sortPriority: index + 1};
			}),
		};
	}, [items]);

	const onRenderRow: IDetailsListProps['onRenderRow'] = propsR => {
		const customStyles: Partial<IDetailsRowStyles> = {};
		if (propsR) {
			customStyles.root = {backgroundColor: theme.palette.themeLighterAlt};
			if (changedItems.at(propsR.itemIndex) === true) {
				customStyles.root = {backgroundColor: theme.palette.themeTertiary};
			}
			return <DetailsRow {...propsR} styles={customStyles} />;
		}
		return null;
	};

	const dialogContentProps = {
		type: DialogType.normal,
		title: getTranslation(t, 'DragDropSortEnd'),
		subText: getTranslation(t, 'SubText'),
	};

	useCommand(
		{
			key: 'dragDropSortEnd',
			text: getTranslation(t, 'DragDropSortEnd'),
			iconProps: {iconName: 'SaveAndClose'},
			priority: 5,
			onClick: showDialog,
		},
		[showDialog],
	);

	useCommand(
		{
			key: 'forceAlphabetic',
			text: getTranslation(t, 'ForceAlphabetic'),
			iconProps: {iconName: 'Sort'},
			priority: 6,
			onClick: forceAlphabetic,
		},
		[showDialog],
	);

	const handleSave = React.useCallback(() => {
		items.forEach((x, i) => {
			if (changedItems.at(i) === true) {
				updateEntity({...x, sortPriority: i + 1});
			}
		});
		toggleDragDropSort();
	}, [items, changedItems]);

	return (
		<>
			<Dialog
				hidden={!dialogVisible}
				dialogContentProps={dialogContentProps}
				modalProps={{isBlocking: true}}
			>
				<DialogFooter>
					<PrimaryButton
						onClick={handleSave}
						text={getTranslation(t, 'DragDropSortEnd')}
					/>
					<DefaultButton
						onClick={toggleDragDropSort}
						text={getTranslation(t, 'Cancel')}
					/>
				</DialogFooter>
			</Dialog>
			<EntityList
				dragDropEvents={dragDropEvents}
				selectionMode={SelectionMode.none}
				onRenderRow={onRenderRow}
				{...propsFinal}
			/>
		</>
	);
}

const getTranslation = (t: TFunction, word: string) => {
	return t(word, {
		keyPrefix: 'EntityPageSort',
	});
};
